import { format } from "date-fns";
import type { ReactNode } from "react";

import { useAuth } from "src/providers/AuthProvider";
import { getCustomizationValue, renderCurrency } from "src/lib/utils";
import type { SettlementReceipt } from "src/types/settlement";

const Row = ({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) => (
	<div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-1.5">
		<span className={strong ? "tw-text-[13.5px] tw-font-semibold tw-text-text" : "tw-text-[13px] tw-text-text-soft"}>{label}</span>
		<span className={strong ? "tw-text-[15px] tw-font-bold tw-text-text" : "tw-text-[13.5px] tw-capitalize tw-text-text"}>{value || "—"}</span>
	</div>
);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
	<section>
		<h4 className="tw-mb-2 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">{title}</h4>
		<div className="tw-rounded-[10px] tw-border tw-border-border tw-px-4 tw-py-2">{children}</div>
	</section>
);

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "PPP");
};

interface SettlementDetailsModalProps {
	settlement: SettlementReceipt;
}

const SettlementDetailsModal = ({ settlement }: SettlementDetailsModalProps) => {
	const { user } = useAuth();

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const invoiceLabel = getCustomizationValue(user, "invoice_label", true) || "Invoice";

	return (
		<div className="tw-flex tw-flex-col tw-gap-5">
			<Section title="Contact details">
				<Row label={`${customerLabel} name`} value={[settlement?.first_name, settlement?.last_name].filter(Boolean).join(" ")} />
				<Row label="Business name" value={settlement?.business_name} />
				<Row label={`${customerLabel} ID`} value={settlement?.customer_id} />
			</Section>

			<Section title={`${invoiceLabel} details`}>
				<Row label={`${invoiceLabel} number`} value={settlement?.invoice_id} />
				<Row label="Due date" value={formatDate(settlement?.due_date)} />
				<div className="tw-my-1.5 tw-h-px tw-bg-border" />
				<Row label="Mode of payment" value={settlement?.payment_method} />
				<Row label="Gateway" value={settlement?.payment_provider} />
				<div className="tw-my-1.5 tw-h-px tw-bg-border" />
				<Row label="Subtotal" value={renderCurrency(settlement?.amount ?? 0)} />
				<Row label="VAT" value={renderCurrency(settlement?.vat ?? 0)} />
				<Row label="Transaction fee" value={renderCurrency(settlement?.transaction_fee ?? 0)} />
				<div className="tw-my-1.5 tw-h-px tw-bg-border" />
				<Row label="Total" value={renderCurrency(settlement?.transaction_amount ?? 0)} strong />
			</Section>
		</div>
	);
};

export default SettlementDetailsModal;
